import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { theme, media } from '../../styles/theme';
import { BuylistFilters, PriceFilter, PriceRangeResponse } from '../../types';

interface BuylistFilterSidebarProps {
  filters: BuylistFilters;
  onFilterChange: (filters: BuylistFilters) => void;
  priceRange: PriceRangeResponse | null;
  priceFilter: PriceFilter;
  onPriceFilterChange: (priceFilter: PriceFilter) => void;
  expansionNames: string[];
  selectedExpansion: string;
  onExpansionChange: (expansionName: string) => void;
}

const SidebarContainer = styled.aside`
  width: 260px;
  flex-shrink: 0;

  ${media.tablet} {
    width: 100%;
  }
`;

const SidebarCard = styled.div`
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.md};
  box-shadow: ${theme.shadows.card};
  overflow: visible;
  position: sticky;
  top: calc(var(--tp-header-height) + ${theme.spacing.md});
`;

const SidebarHeader = styled.div`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: ${theme.borderRadius.md} ${theme.borderRadius.md} 0 0;
`;

const SidebarTitle = styled.h3`
  margin: 0;
  font-size: ${theme.fontSizes.md};
  font-weight: ${theme.fontWeights.medium};
`;

const MobileToggle = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${theme.colors.white};
  font-size: ${theme.fontSizes.lg};
  cursor: pointer;
  padding: 0;
  line-height: 1;

  ${media.tablet} {
    display: block;
  }
`;

const FilterBody = styled.div<{ isOpen: boolean }>`
  padding: ${theme.spacing.sm} ${theme.spacing.md} ${theme.spacing.md};

  ${media.tablet} {
    display: ${props => props.isOpen ? 'block' : 'none'};
  }
`;

const FilterSection = styled.div`
  padding: ${theme.spacing.xs} 0;
`;

const FilterSectionTitle = styled.p`
  font-size: ${theme.fontSizes.xs};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${theme.colors.gray.medium};
  margin: 0 0 ${theme.spacing.xs} 0;
  font-weight: ${theme.fontWeights.semibold};
`;

const FilterOption = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: 8px 10px;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  transition: background-color ${theme.transitions.fast};
  user-select: none;

  &:hover {
    background-color: ${theme.colors.gray.lightest};
  }
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: ${theme.colors.primary};
  flex-shrink: 0;
`;

const FilterLabel = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text};
  line-height: 1.3;
`;

const FilterDescription = styled.span`
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.gray.dark};
  display: block;
  margin-top: 2px;
`;

// --- Price Range Slider Styles ---

const PriceFilterSection = styled.div`
  padding: ${theme.spacing.xs} 0;
  border-top: 1px solid ${theme.colors.gray.light};
  margin-top: ${theme.spacing.xs};
`;

const SliderContainer = styled.div`
  position: relative;
  height: 36px;
  margin-top: ${theme.spacing.xs};
`;

const SliderTrack = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 4px;
  transform: translateY(-50%);
  background-color: ${theme.colors.gray.light};
  border-radius: ${theme.borderRadius.sm};
`;

const SliderRange = styled.div<{ left: number; right: number }>`
  position: absolute;
  top: 50%;
  height: 4px;
  transform: translateY(-50%);
  background-color: ${theme.colors.primary};
  border-radius: ${theme.borderRadius.sm};
  left: ${props => props.left}%;
  right: ${props => props.right}%;
`;

const thumbStyles = `
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: ${theme.colors.primary};
  border: 2px solid ${theme.colors.white};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  pointer-events: all;
  transition: transform 0.1s ease;

  &:hover {
    transform: scale(1.15);
  }

  &:active {
    transform: scale(1.25);
  }
`;

const RangeInput = styled.input.attrs({ type: 'range' })`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  pointer-events: none;
  margin: 0;
  padding: 0;
  z-index: 2;

  &::-webkit-slider-thumb {
    ${thumbStyles}
  }

  &::-moz-range-thumb {
    ${thumbStyles}
  }

  /* Ensure the max slider thumb sits above the min slider */
  &:nth-child(4) {
    z-index: 3;
  }

  &::-webkit-slider-runnable-track {
    -webkit-appearance: none;
    height: 0;
  }

  &::-moz-range-track {
    height: 0;
    background: transparent;
  }
`;

const SliderLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
`;

const SliderValue = styled.span<{ active?: boolean }>`
  font-size: ${theme.fontSizes.xs};
  font-weight: ${props => props.active ? theme.fontWeights.semibold : theme.fontWeights.regular};
  color: ${props => props.active ? theme.colors.primary : theme.colors.gray.dark};
`;

const SliderPlaceholder = styled.div`
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.gray.medium};
  font-size: ${theme.fontSizes.sm};
`;

// --- End Slider Styles ---

const ExpansionFilterSection = styled.div`
  padding: ${theme.spacing.xs} 0;
  border-top: 1px solid ${theme.colors.gray.light};
  margin-top: ${theme.spacing.xs};
`;

const ExpansionSelect = styled.select`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text};
  background-color: ${theme.colors.white};
  cursor: pointer;
  transition: border-color ${theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const ClearButton = styled.button`
  width: 100%;
  padding: ${theme.spacing.xs};
  margin-top: ${theme.spacing.xs};
  background: none;
  border: 1px solid ${theme.colors.gray.light};
  border-radius: ${theme.borderRadius.sm};
  color: ${theme.colors.gray.dark};
  font-size: ${theme.fontSizes.sm};
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background-color: ${theme.colors.gray.lightest};
    border-color: ${theme.colors.gray.medium};
    color: ${theme.colors.text};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
    &:hover {
      background: none;
      border-color: ${theme.colors.gray.light};
      color: ${theme.colors.gray.dark};
    }
  }
`;

const ActiveCount = styled.span`
  background-color: ${theme.colors.primary};
  color: ${theme.colors.white};
  font-size: ${theme.fontSizes.xs};
  padding: 2px 8px;
  border-radius: ${theme.borderRadius.full};
  font-weight: ${theme.fontWeights.semibold};
`;

interface FilterOptionConfig {
  key: keyof BuylistFilters;
  label: string;
  description: string;
}

const filterOptions: FilterOptionConfig[] = [
  {
    key: 'reservedList',
    label: 'Reserved List',
    description: 'Kort som aldri trykkes igjen',
  },
  {
    key: 'gameChanger',
    label: 'Game Changer',
    description: 'Format-definerende kort',
  },
  {
    key: 'topCommander',
    label: 'Topp Commander',
    description: 'Mest spilte commander-kort',
  },
  {
    key: 'topEdhCard',
    label: 'Topp EDH-kort',
    description: 'Populære EDH-staples',
  },
  {
    key: 'premodernStaple',
    label: 'Premodern Staple',
    description: 'Populære Premodern-kort',
  },
  {
    key: 'modernStaple',
    label: 'Modern Staple',
    description: 'Populære Modern-kort',
  },
];

const BuylistFilterSidebar: React.FC<BuylistFilterSidebarProps> = ({
  filters,
  onFilterChange,
  priceRange,
  priceFilter,
  onPriceFilterChange,
  expansionNames,
  selectedExpansion,
  onExpansionChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Slider local state — derived from priceRange bounds
  const rangeMin = priceRange ? Math.floor(priceRange.minPris) : 0;
  const rangeMax = priceRange ? Math.ceil(priceRange.maxPris) : 100;

  const [localMin, setLocalMin] = useState(rangeMin);
  const [localMax, setLocalMax] = useState(rangeMax);

  // Initialize local slider state when priceRange loads
  useEffect(() => {
    if (priceRange) {
      const min = Math.floor(priceRange.minPris);
      const max = Math.ceil(priceRange.maxPris);
      setLocalMin(priceFilter.minPrice ?? min);
      setLocalMax(priceFilter.maxPrice ?? max);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceRange]);

  // Sync sliders when priceFilter is cleared externally (e.g. "Fjern alle filter")
  useEffect(() => {
    if (priceFilter.minPrice === undefined && priceFilter.maxPrice === undefined && priceRange) {
      setLocalMin(Math.floor(priceRange.minPris));
      setLocalMax(Math.ceil(priceRange.maxPris));
    }
  }, [priceFilter.minPrice, priceFilter.maxPrice, priceRange]);

  // Debounce slider changes — propagate after 300ms of inactivity
  const emitPriceFilter = useCallback((min: number, max: number) => {
    if (!priceRange) return;
    const boundMin = Math.floor(priceRange.minPris);
    const boundMax = Math.ceil(priceRange.maxPris);

    // Only send filter when the user has narrowed the range from its bounds
    const newFilter: PriceFilter = {
      minPrice: min > boundMin ? min : undefined,
      maxPrice: max < boundMax ? max : undefined,
    };
    onPriceFilterChange(newFilter);
  }, [priceRange, onPriceFilterChange]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      emitPriceFilter(localMin, localMax);
    }, 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localMin, localMax]);

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.min(Number(e.target.value), localMax);
    setLocalMin(value);
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.max(Number(e.target.value), localMin);
    setLocalMax(value);
  };

  // Calculate active range bar position as percentages
  const totalRange = rangeMax - rangeMin || 1;
  const leftPercent = ((localMin - rangeMin) / totalRange) * 100;
  const rightPercent = ((rangeMax - localMax) / totalRange) * 100;

  const categoryActiveCount = Object.values(filters).filter(Boolean).length;
  const priceIsActive = priceRange
    ? (localMin > Math.floor(priceRange.minPris) || localMax < Math.ceil(priceRange.maxPris))
    : false;
  const expansionIsActive = selectedExpansion !== '';
  const activeCount = categoryActiveCount + (priceIsActive ? 1 : 0) + (expansionIsActive ? 1 : 0);

  const handleToggle = (key: keyof BuylistFilters) => {
    onFilterChange({ ...filters, [key]: !filters[key] });
  };

  const handleClearAll = () => {
    onFilterChange({
      reservedList: false,
      gameChanger: false,
      topCommander: false,
      topEdhCard: false,
      premodernStaple: false,
      modernStaple: false,
    });
    onPriceFilterChange({ minPrice: undefined, maxPrice: undefined });
    onExpansionChange('');
    if (priceRange) {
      setLocalMin(Math.floor(priceRange.minPris));
      setLocalMax(Math.ceil(priceRange.maxPris));
    }
  };

  return (
    <SidebarContainer>
      <SidebarCard>
        <SidebarHeader>
          <SidebarTitle>
            Filter {activeCount > 0 && <ActiveCount>{activeCount}</ActiveCount>}
          </SidebarTitle>
          <MobileToggle onClick={() => setIsOpen(!isOpen)} aria-label="Vis/skjul filter">
            {isOpen ? '✕' : '☰'}
          </MobileToggle>
        </SidebarHeader>

        <FilterBody isOpen={isOpen}>
          <FilterSection>
            <FilterSectionTitle>Kategorier</FilterSectionTitle>
            {filterOptions.map((option) => (
              <FilterOption key={option.key}>
                <Checkbox
                  checked={filters[option.key]}
                  onChange={() => handleToggle(option.key)}
                />
                <div>
                  <FilterLabel>{option.label}</FilterLabel>
                  <FilterDescription>{option.description}</FilterDescription>
                </div>
              </FilterOption>
            ))}
          </FilterSection>

          <ExpansionFilterSection>
            <FilterSectionTitle>Ekspansjon</FilterSectionTitle>
            <ExpansionSelect
              value={selectedExpansion}
              onChange={(e) => onExpansionChange(e.target.value)}
            >
              <option value="">Alle ekspansjoner</option>
              {expansionNames.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </ExpansionSelect>
          </ExpansionFilterSection>

          <PriceFilterSection>
            <FilterSectionTitle>Prisintervall</FilterSectionTitle>
            {priceRange ? (
              <>
                <SliderContainer>
                  <SliderTrack />
                  <SliderRange left={leftPercent} right={rightPercent} />
                  <RangeInput
                    min={rangeMin}
                    max={rangeMax}
                    value={localMin}
                    onChange={handleMinChange}
                  />
                  <RangeInput
                    min={rangeMin}
                    max={rangeMax}
                    value={localMax}
                    onChange={handleMaxChange}
                  />
                </SliderContainer>
                <SliderLabels>
                  <SliderValue active={localMin > rangeMin}>{localMin} kr</SliderValue>
                  <SliderValue active={localMax < rangeMax}>{localMax} kr</SliderValue>
                </SliderLabels>
              </>
            ) : (
              <SliderPlaceholder>Laster...</SliderPlaceholder>
            )}
          </PriceFilterSection>

          <ClearButton onClick={handleClearAll} disabled={activeCount === 0}>
            Fjern alle filter
          </ClearButton>
        </FilterBody>
      </SidebarCard>
    </SidebarContainer>
  );
};

export default BuylistFilterSidebar;
